import { useCallback, useEffect, useRef, useState } from "react";
import {
  MAIL_AUTH_LOST_EVENT,
  MAIL_AUTH_READY_EVENT,
} from "@/auth/constants";
import {
  MAIL_BOOT_ARCHIVE_STATS_TIMEOUT_MS,
  MAIL_BOOT_FADE_MS,
  MAIL_BOOT_MIN_MS,
  MAIL_BOOT_OPT_MIN_MISSING,
  MAIL_BOOT_OPTIMIZATION_STALL_MS,
  MAIL_BOOT_SESSION_KEY,
  MAIL_BOOT_STEPS,
  WELCOME_BOOT_SEQUENCE,
  markOptOverlayDoneToday,
  readOptOverlayDoneToday,
  type MailBootStepStatus,
} from "@/lib/boot/mailBootConstants";
import type { MailBootStepView } from "@/features/boot/MailBootScreen";
import {
  markInboxArchiveInflight,
  markInboxArchiveKicked,
} from "@/lib/storage/inboxArchiveSession";
import { syncInboxServerStorage } from "@/lib/storage/inboxLocalStorageSync";
import { fetchMailStorageStats } from "@/lib/api/storage";
import {
  getFolderStorageProgress,
  subscribeFolderStorageProgress,
} from "@/lib/storage/folderStorageProgress";

export type MailBootOptimization = {
  pending: number;
  done: number;
  total: number;
};

function markSessionBootDone(): void {
  try {
    sessionStorage.setItem(MAIL_BOOT_SESSION_KEY, "1");
  } catch {
    /* ignore */
  }
}

export function buildWelcomeSteps(activeIndex: number): MailBootStepView[] {
  return MAIL_BOOT_STEPS.map((def) => {
    const seqIndex = WELCOME_BOOT_SEQUENCE.indexOf(def.id);
    if (seqIndex < 0) {
      return { ...def, status: "skipped" as MailBootStepStatus };
    }
    let status: MailBootStepStatus = "pending";
    if (seqIndex < activeIndex) status = "done";
    else if (seqIndex === activeIndex) status = "active";
    return { ...def, status };
  });
}

function buildIdleSteps(): MailBootStepView[] {
  return MAIL_BOOT_STEPS.map((step) => ({
    id: step.id,
    label: step.label,
    hint: step.hint,
    status: "skipped" as MailBootStepStatus,
  }));
}

type Options = {
  accountsLoading: boolean;
  accountsError: string | null;
  hasAccount: boolean;
  accountsHasList?: boolean;
  accountId?: number;
  foldersLoading: boolean;
  foldersHasList?: boolean;
  inboxListSettled?: boolean;
  enabled?: boolean;
};

function readAuthReadyFlag(): boolean {
  return typeof window !== "undefined" && window.__mailSessionVerified === true;
}

const archivedAccounts = new Set<number>();

/** Premier splash de ce document. Changer de largeur remonte l’autre coquille :
 *  le splash ne repart pas. Recharger la page remet ça à zéro. */
let mailSplashSeen = false;

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

async function kickSilentInboxArchive(accountId: number): Promise<void> {
  if (archivedAccounts.has(accountId)) return;

  const statsRes = await Promise.race([
    fetchMailStorageStats(accountId),
    sleep(MAIL_BOOT_ARCHIVE_STATS_TIMEOUT_MS).then(() => null),
  ]);

  if (statsRes == null || !statsRes.ok) return;

  const missing = statsRes.data.inboxMissing ?? 0;
  const upToDate = statsRes.data.inboxArchiveUpToDate === true || missing <= 0;
  if (upToDate || missing <= 0) {
    archivedAccounts.add(accountId);
    return;
  }

  archivedAccounts.add(accountId);
  markInboxArchiveKicked(accountId);
  markInboxArchiveInflight(accountId, true);
  void syncInboxServerStorage(accountId, "INBOX", []).finally(() => {
    markInboxArchiveInflight(accountId, false);
  });
}

/**
 * Phase 2 — splash 5 étapes post-SSO, puis app + overlay optimisation .eml optionnel.
 */
export function useMailAppBoot(options: Options) {
  const optionsRef = useRef(options);
  optionsRef.current = options;
  const enabled = options.enabled ?? true;

  const [authReady, setAuthReady] = useState(readAuthReadyFlag);

  useEffect(() => {
    const sync = () => setAuthReady(readAuthReadyFlag());
    const onLost = () => {
      mailSplashSeen = false;
      sync();
    };
    sync();
    window.addEventListener(MAIL_AUTH_READY_EVENT, sync);
    window.addEventListener(MAIL_AUTH_LOST_EVENT, onLost);
    return () => {
      window.removeEventListener(MAIL_AUTH_READY_EVENT, sync);
      window.removeEventListener(MAIL_AUTH_LOST_EVENT, onLost);
    };
  }, []);

  const [splashDone, setSplashDone] = useState(() => mailSplashSeen);
  const [splashExiting, setSplashExiting] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [steps, setSteps] = useState<MailBootStepView[]>(() => buildWelcomeSteps(0));
  const [progress, setProgress] = useState(8);
  const [detail, setDetail] = useState<string | null>(null);
  const splashBornRef = useRef(0);

  const [optExiting, setOptExiting] = useState(false);
  const [optimization, setOptimization] = useState<MailBootOptimization | null>(null);

  const overlayCheckedRef = useRef(false);
  const syncStartedRef = useRef(false);
  const finishTimerRef = useRef<number | null>(null);
  const [minElapsed, setMinElapsed] = useState(false);

  const waitingAccounts =
    authReady &&
    options.accountsLoading === true &&
    options.accountsHasList !== true &&
    !options.accountsError;

  const waitingAuth = !authReady;

  useEffect(() => {
    if (!enabled || splashDone) return;
    mailSplashSeen = true;
    if (splashBornRef.current < 1) {
      splashBornRef.current = Date.now();
    }
    const last = WELCOME_BOOT_SEQUENCE.length - 1;
    setStepIndex(last);
    setSteps(buildWelcomeSteps(last));
    setProgress(100);
    const wait = Math.max(0, MAIL_BOOT_MIN_MS - (Date.now() - splashBornRef.current));
    const timer = window.setTimeout(() => setMinElapsed(true), wait);
    return () => window.clearTimeout(timer);
  }, [enabled, splashDone]);

  useEffect(() => {
    if (!minElapsed || !authReady || splashDone || splashExiting) return;
    setSplashExiting(true);
  }, [minElapsed, authReady, splashDone, splashExiting]);

  useEffect(() => {
    if (!splashExiting) return;
    finishTimerRef.current = window.setTimeout(() => {
      markSessionBootDone();
      mailSplashSeen = true;
      setSplashDone(true);
      setSplashExiting(false);
      splashBornRef.current = 0;
    }, MAIL_BOOT_FADE_MS);
    return () => {
      if (finishTimerRef.current != null) window.clearTimeout(finishTimerRef.current);
    };
  }, [splashExiting]);

  const dismissOverlay = useCallback(() => {
    markOptOverlayDoneToday();
    setOptExiting(true);
    window.setTimeout(() => {
      setOptimization(null);
      setOptExiting(false);
    }, MAIL_BOOT_FADE_MS);
  }, []);

  const skipToBackground = dismissOverlay;

  const startInboxArchive = useCallback((accountId: number, missing: number) => {
    if (syncStartedRef.current) return;
    syncStartedRef.current = true;
    markInboxArchiveKicked(accountId);
    markInboxArchiveInflight(accountId, true);
    setOptimization({ pending: missing, done: 0, total: missing });
    setProgress(0);
    setDetail(`0 / ${missing}`);

    void syncInboxServerStorage(accountId, "INBOX", []).finally(() => {
      markInboxArchiveInflight(accountId, false);
    });
  }, []);

  useEffect(() => {
    if (!enabled || !splashDone || overlayCheckedRef.current) return;
    overlayCheckedRef.current = true;

    let cancelled = false;

    void (async () => {
      let accountId = optionsRef.current.accountId;
      const deadline = Date.now() + 4_000;
      while (accountId == null && Date.now() < deadline && !cancelled) {
        await sleep(100);
        accountId = optionsRef.current.accountId;
      }

      if (cancelled || accountId == null || !optionsRef.current.hasAccount) return;

      if (readOptOverlayDoneToday()) {
        void kickSilentInboxArchive(accountId);
        return;
      }

      const statsRes = await Promise.race([
        fetchMailStorageStats(accountId),
        sleep(MAIL_BOOT_ARCHIVE_STATS_TIMEOUT_MS).then(() => null),
      ]);

      if (cancelled) return;

      const missing =
        statsRes != null && statsRes.ok ? statsRes.data.inboxMissing ?? 0 : 0;
      const upToDate =
        statsRes != null &&
        statsRes.ok &&
        (statsRes.data.inboxArchiveUpToDate === true || missing <= 0);

      if (upToDate || missing < MAIL_BOOT_OPT_MIN_MISSING || statsRes == null || !statsRes.ok) {
        if (missing >= MAIL_BOOT_OPT_MIN_MISSING && statsRes != null && statsRes.ok) {
          void kickSilentInboxArchive(accountId);
        }
        return;
      }

      startInboxArchive(accountId, missing);
    })();

    return () => {
      cancelled = true;
    };
  }, [enabled, splashDone, startInboxArchive]);

  useEffect(() => {
    const accountId = options.accountId;
    if (optimization == null || accountId == null) return;

    const syncFromProgress = () => {
      const row = getFolderStorageProgress(accountId);
      if (row.total <= 0) return;

      const done = row.done;
      const total = row.total;
      setOptimization({ pending: Math.max(0, total - done), done, total });
      setProgress(row.progress);
      setDetail(`${done} / ${total}`);

      if (!row.active && done >= total) {
        markOptOverlayDoneToday();
        window.setTimeout(() => dismissOverlay(), 500);
      }
    };

    syncFromProgress();
    const stallTimer = window.setTimeout(() => dismissOverlay(), MAIL_BOOT_OPTIMIZATION_STALL_MS);
    const unsub = subscribeFolderStorageProgress(syncFromProgress);

    return () => {
      window.clearTimeout(stallTimer);
      unsub();
    };
  }, [optimization, options.accountId, dismissOverlay]);

  useEffect(() => {
    if (!splashDone) return;
    const accountId = options.accountId;
    if (accountId == null || !options.hasAccount) return;
    void kickSilentInboxArchive(accountId);
  }, [splashDone, options.accountId, options.hasAccount]);

  const optSteps =
    optimization != null
      ? MAIL_BOOT_STEPS.map((def) => ({
          ...def,
          status: (def.id === "optimization"
            ? "active"
            : def.id === "ready"
              ? "pending"
              : "done") as MailBootStepStatus,
        }))
      : buildIdleSteps();

  const cacheWarm =
    options.accountsHasList === true &&
    (!options.hasAccount ||
      (options.foldersHasList === true && options.inboxListSettled === true));

  const accountsStepIndex = Math.max(0, WELCOME_BOOT_SEQUENCE.indexOf("accounts"));
  const waitingAccountsSteps = buildWelcomeSteps(accountsStepIndex);
  const waitingAuthSteps = buildWelcomeSteps(0);

  const showSplash = enabled && !splashDone && authReady;
  const showOptOverlay = optimization != null || optExiting;
  const showOverlay = (enabled && !splashDone) || showOptOverlay;

  const label = showOptOverlay
    ? detail ?? "Préparation de vos messages…"
    : waitingAuth
      ? "Démarrage…"
      : waitingAccounts
        ? "Vérification de la session…"
        : "Démarrage…";

  return {
    ready: splashDone && (cacheWarm || (authReady && (!waitingAccounts || mailSplashSeen))),
    cacheWarm,
    exiting: splashExiting || optExiting,
    label,
    steps: showSplash
      ? steps
      : waitingAccounts
        ? waitingAccountsSteps
        : waitingAuth
          ? waitingAuthSteps
          : optSteps,
    progress: showSplash
      ? progress
      : waitingAccounts
        ? 28
        : waitingAuth
          ? 8
          : optimization != null
            ? progress
            : 100,
    detail: showOptOverlay ? detail : null,
    indeterminate: (showSplash && stepIndex === 0) || waitingAuth,
    optimization,
    skipToBackground,
    showOverlay,
  };
}
